// @flow @format
import React from 'react';
import PlayIcon from '../Icon/play-circle-o';
import PauseIcon from '../Icon/pause-circle-o';
import SuccessIcon from '../Icon/check-circle';
import LoadingIcon from '../Icon/circle-o-notch';
import DownloadIcon from '../Icon/cloud-download';
import WarningIcon from '../Icon/exclamation-triangle';
import TrashIcon from '../Icon/trash';
import ProgressBar from '../ProgressBar';
import {toSIString, toPercentage} from 'lib/util';
import {
	DOWNLOAD_PAUSING_STATUS,
	DOWNLOAD_PAUSED_STATUS,
	DOWNLOAD_SETUP_STATUS,
	DOWNLOAD_RUNNING_STATUS,
	DOWNLOAD_COMPLETED_STATUS,
	DOWNLOAD_FAILED_STATUS,
	DOWNLOAD_RETRY_STATUS,
} from 'lib/downloader';
import './Download.css';

import type {DownloadSummary} from 'lib/downloader';

type Props = {
	download: DownloadSummary,
	throughput: number,
	actions: {
		startDownload: (id: string) => void,
		pauseDownload: (id: string) => void,
		deleteDownload: (id: string) => void,
		saveDownload: (id: string) => void,
	},
};

const statusTexts = {
	[DOWNLOAD_PAUSING_STATUS]: 'Pausando...',
	[DOWNLOAD_SETUP_STATUS]: 'Solicitando información...',
	[DOWNLOAD_FAILED_STATUS]: 'Descarga fallida',
	[DOWNLOAD_RETRY_STATUS]: 'Reintentando...',
	[DOWNLOAD_COMPLETED_STATUS]: '',
	[DOWNLOAD_RUNNING_STATUS]: '',
	[DOWNLOAD_PAUSED_STATUS]: 'Pausado',
};

const statusIcons = {
	[DOWNLOAD_PAUSING_STATUS]: null,
	[DOWNLOAD_SETUP_STATUS]: null,
	[DOWNLOAD_FAILED_STATUS]: null,
	[DOWNLOAD_RETRY_STATUS]: <LoadingIcon className="rotate-animation" />,
	[DOWNLOAD_COMPLETED_STATUS]: null,
	[DOWNLOAD_RUNNING_STATUS]: null,
	[DOWNLOAD_PAUSED_STATUS]: null,
};

export const ActiveDownload = ({download, throughput, actions}: Props) => {
	const createdAt = new Date(download.createdAt);

	return (
		<div className="Download">
			<HoverButtons>
				{download.status === DOWNLOAD_COMPLETED_STATUS && (
					<HoverButton
						icon={<DownloadIcon />}
						onClick={() => actions.saveDownload(download.id)}
					/>
				)}
				{(download.status === DOWNLOAD_COMPLETED_STATUS ||
					download.status === DOWNLOAD_FAILED_STATUS ||
					download.status === DOWNLOAD_PAUSED_STATUS) && (
					<HoverButton
						icon={<TrashIcon />}
						onClick={() => actions.deleteDownload(download.id)}
					/>
				)}
			</HoverButtons>
			<IconArea
				status={download.status}
				onClick={() => {
					if (
						download.status === DOWNLOAD_RUNNING_STATUS ||
						download.status === DOWNLOAD_RETRY_STATUS
					) {
						actions.pauseDownload(download.id);
						return;
					}

					if (download.status === DOWNLOAD_PAUSED_STATUS) {
						actions.startDownload(download.id);
						return;
					}
				}}
			/>
			<InfoArea>
				<InfoAreaSmallText
				>{`${createdAt.toLocaleTimeString()} - ${createdAt.toLocaleDateString()}`}</InfoAreaSmallText>
				<InfoAreaFilename>{download.filename}</InfoAreaFilename>
				<InfoAreaSmallText>{download.owner}</InfoAreaSmallText>
				<InfoAreaText>
					<b>
						{statusIcons[download.status]}{' '}
						{statusTexts[download.status]}
					</b>
				</InfoAreaText>
				<InfoAreaText>
					{toSIString(download.downloaded)} /{' '}
					{toSIString(download.size)}{' '}
					<b>
						({toPercentage(download.downloaded, download.size)})
					</b>{' '}
					{download.status === DOWNLOAD_COMPLETED_STATUS
						? 0
						: toSIString(throughput, 'b')}/s
				</InfoAreaText>

				{download.status !== DOWNLOAD_COMPLETED_STATUS && (
					<InfoAreaProgressBar
						completed={
							download.downloaded / (download.size || 1) * 100
						}
					/>
				)}
			</InfoArea>
		</div>
	);
};

const InfoArea = ({children}) => (
	<div className="Download__InfoArea">{children}</div>
);
const InfoAreaFilename = ({children}) => (
	<p className="Download__InfoArea__filename">{children}</p>
);
const InfoAreaText = ({children}) => (
	<p className="Download__InfoArea__text">{children}</p>
);
const InfoAreaSmallText = ({children}) => (
	<p className="Download__InfoArea__small-text">{children}</p>
);
const InfoAreaProgressBar = ({completed}) => (
	<ProgressBar
		className="Download__InfoArea__progressBar"
		completed={completed}
	/>
);

const HoverButtons = ({children}) => (
	<div className="DownloadHoverButtons">{children}</div>
);

const HoverButton = ({icon, onClick}) => (
	<button className="DownloadHoverButton" onClick={onClick}>
		{icon}
	</button>
);

const statusButtons = {
	[DOWNLOAD_PAUSING_STATUS]: (
		<LoadingIcon className="Download__IconArea__icon rotate-animation" />
	),
	[DOWNLOAD_SETUP_STATUS]: (
		<LoadingIcon className="Download__IconArea__icon rotate-animation" />
	),
	[DOWNLOAD_FAILED_STATUS]: (
		<WarningIcon className="Download__IconArea__icon Download__IconArea__icon--failed" />
	),
	[DOWNLOAD_RETRY_STATUS]: (
		<PauseIcon className="Download__IconArea__button" />
	),
	[DOWNLOAD_COMPLETED_STATUS]: (
		<SuccessIcon className="Download__IconArea__icon Download__IconArea__icon--successed" />
	),
	[DOWNLOAD_RUNNING_STATUS]: (
		<PauseIcon className="Download__IconArea__button" />
	),
	[DOWNLOAD_PAUSED_STATUS]: (
		<PlayIcon className="Download__IconArea__button" />
	),
};

const IconArea = ({status, onClick}) => {
	const icon = statusButtons[status];
	return (
		<div className="Download__IconArea">
			<icon.type {...icon.props} onClick={onClick} />
		</div>
	);
};



// WEBPACK FOOTER //
// ./src/components/DownloadHistory/Download.js